import { formatItemName } from '../../../lib/buy/formatItemName';
import { Deal, Plan } from '../../../types/plans';
import { UserBillingInfo } from '../BillingAddress';

/**
 * Build standardized ecommerce item for GA4 tracking.
 * Single source of truth for all GA4 events (view_item, add_to_cart, begin_checkout, purchase, etc).
 * @param plan Product plan details.
 * @param includeGtmFields Add extra GTM-specific fields (currency, unit_amount, subscription_period).
 * @returns Standardized ecommerce item object.
 */
export function buildEcommerceItem(plan: Plan, includeGtmFields = false) {
  const getProductCode = (code: string) => code?.split('-')[0] || 'n/a';

  const base: any = {
    item_id: plan.inventoryID || plan.hash,
    item_name: formatItemName(plan),
    item_category: getProductCode(plan.code),
    item_variant: plan.code,
    item_brand: 'Osmosis',
    price: plan.currency.USDUnitAmount || plan.currency.unitAmount,
    quantity: 1,
  };

  if (includeGtmFields) {
    base.currency = plan.currency.currency || 'USD';
    base.unit_amount = plan.currency.unitAmount || null;
    base.subscription_period = plan.intervalLength || null;
  }

  return base;
}

/**
 * Build Add Payment Info tracking event.
 */
export function buildAddPaymentInfo(
  plan: Plan,
  params: { paymentMethod: string; currency: string; orderTotal: number },
  deal?: Deal,
) {
  return {
    event: 'add_payment_info',
    type: 'gtm',
    properties: {
      customer_data: {
        email: '',
      },
      currency: params.currency,
      value: params.orderTotal,
      tax: 0,
      shipping: 0,
      coupon: deal?.url || '',
      payment_type: params.paymentMethod,
      step: '3',
      description: 'Add Payment Info',
      ecommerce: {
        items: [buildEcommerceItem(plan)],
      },
    },
  };
}

/**
 * Build Add Shipping Info tracking event.
 */
export function buildAddShippingInfo(plan: Plan, userAddress: UserBillingInfo) {
  const id = plan?.inventoryID || plan?.hash;
  return {
    ecommerce: {
      checkout: {
        actionField: {
          step: '2',
          description: 'Add Shipping Info',
          option: `${id}+ logged`,
          items: [buildEcommerceItem(plan)],
        },
      },
    },
  };
}

/**
 * Build Remove From Cart tracking event.
 */
export function buildRemoveFromCart(plan: Plan) {
  return {
    ecommerce: {
      currencyCode: plan?.currency.currency || 'USD',
      remove: {
        items: [buildEcommerceItem(plan)],
      },
    },
  };
}
